import React, { useState, useRef } from 'react';
import { shareReportAsPdf, downloadReportAsPdf } from '../../../utils/reportPdfShare';
import { CheckCircle2, XCircle, MinusCircle, ShieldCheck, Share2, Download, Smartphone } from 'lucide-react';

// ── SugarHathi brand palette (matches GRAD in do_finance_new.jsx) ─────────
const ACCENT = '#f97316';
const ACCENT_DARK = '#ea580c';
const BG = '#fffbf5';
const INK = '#1f2937';
const SLATE = '#6b7280';
const LINE = '#f0e6d8';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const GREEN = '#16a34a';
const RED = '#dc2626';

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatMonthYear(month, year) {
  if (!month || !year) return '—';
  const mi = parseInt(month, 10) - 1;
  if (mi < 0 || mi > 11) return `${month}/${year}`;
  return `${MONTH_SHORT[mi]} ${year}`;
}

// Display names for platform keys (raw API keys are lowercase/compressed)
const PLATFORM_LABELS = {
  shaadi: 'Shaadi.com', instagram: 'Instagram', flipkart: 'Flipkart', snapdeal: 'Snapdeal',
  housing: 'Housing.com', facebook: 'Facebook', microsoft: 'Microsoft', whatsapp: 'WhatsApp',
  yatra: 'Yatra', jeevansathi: 'Jeevansathi', indianexpress: 'Indian Express', zoho: 'Zoho',
  digilocker: 'DigiLocker', policybazar: 'PolicyBazaar', nobroker: 'NoBroker', byju: "Byju's", irctc: 'IRCTC',
};
const PLATFORM_ORDER = ['facebook','instagram','whatsapp','microsoft','zoho','digilocker','flipkart','snapdeal','housing','nobroker','irctc','yatra','shaadi','jeevansathi','policybazar','indianexpress','byju'];

// ── Social Intelligence sample response (Decentro) ──────────────────────────
// Note: the phone number itself isn't in the API response body — it's only
// in the request payload. `mobile` here represents what the edge function
// already has from the applicant record, passed through for display.
const SAMPLE = {
  mobile: '95XXXXXX24',
  decentroTxnId: 'F63ABD489A66462FA5AD3CA2D797AC00',
  status: 'SUCCESS',
  responseCode: 'S00000',
  message: 'Social Intelligence Data from Alternate Data Sources Fetched Successfully.',
  data: {
    socialProfilesFound: '10',
    accountDetails: {
      shaadi: {}, instagram: {},
      flipkart: { userExists: 'YES' }, snapdeal: { userExists: 'YES' }, housing: { userExists: 'YES' },
      facebook: { userExists: 'YES' }, microsoft: { userExists: 'YES' },
      whatsapp: { userExists: 'YES', status: '' },
      yatra: { userExists: 'NO' }, jeevansathi: { userExists: 'NO' }, indianexpress: { userExists: 'NO' },
      zoho: { userExists: 'YES' }, digilocker: { userExists: 'YES' },
      policybazar: {}, nobroker: { userExists: 'YES' }, byju: {}, irctc: { userExists: 'YES' },
    },
    digitalAge: { year: '2018', month: '12', age: '7+' },
    phoneMeta: { isPostpaid: 'UNKNOWN', operator: 'Airtel', circle: 'Andhra Pradesh' },
  },
};

// Yes / No / Not Checked (empty object {} means the platform was probed
// but returned no signal — different from an explicit "NO").
function statusOf(entry) {
  if (!entry || entry.userExists === undefined) return 'na';
  return entry.userExists === 'YES' ? 'yes' : 'no';
}

function StatusChip({ state }) {
  const map = {
    yes: { bg: '#f0fdf4', color: GREEN, Icon: CheckCircle2, label: 'Yes' },
    no: { bg: '#fef2f2', color: RED, Icon: XCircle, label: 'No' },
    na: { bg: BG, color: SLATE, Icon: MinusCircle, label: 'N/A' },
  };
  const s = map[state];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 999,
      fontSize: 10.5, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.color}22`,
    }}>
      <s.Icon size={10} strokeWidth={2.75} />
      {s.label}
    </span>
  );
}

function SectionCard({ title, icon: Icon, children, footer }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: '14px 16px', marginBottom: 8 }}>
      <h3 style={{ fontSize: 12, fontWeight: 800, color: ACCENT_DARK, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
        {Icon && <Icon size={13} />}
        {title}
      </h3>
      <div>{children}</div>
      {footer && <div style={{ paddingTop: 10, marginTop: 2 }}>{footer}</div>}
    </div>
  );
}

export default function SocialAdminReport({ data = SAMPLE }) {
  const cardRef = useRef(null);
  const d = data.data;
  const platforms = PLATFORM_ORDER.map(key => ({ key, label: PLATFORM_LABELS[key] || key, state: statusOf(d.accountDetails[key]) }));
  const yesCount = platforms.filter(p => p.state === 'yes').length;
  const noCount = platforms.filter(p => p.state === 'no').length;
  const naCount = platforms.filter(p => p.state === 'na').length;

  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadReportAsPdf(cardRef, 'social-intelligence-admin-report.pdf', BG); } finally { setPdfBusy(false); }
  };
  const handleShare = async () => {
    const shareText = `Social Intelligence Report\nProfiles found: ${d.socialProfilesFound}/${platforms.length}\nPhone digital age: ${d.digitalAge.age} yrs (since ${formatMonthYear(d.digitalAge.month, d.digitalAge.year)})`;
    setPdfBusy(true);
    try { await shareReportAsPdf(cardRef, 'social-intelligence-admin-report.pdf', 'Social Intelligence — Admin Report', shareText, BG); } finally { setPdfBusy(false); }
  };

  return (
    <div style={{ minHeight: '100%', background: BG, padding: '20px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } body { background: #fff !important; } }
        @media (max-width: 360px) { .si-grid { grid-template-columns: 1fr !important; } }
      `}</style>
      <div ref={cardRef} style={{ maxWidth: 580, width: '100%', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button onClick={handleShare} disabled={pdfBusy} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px', color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            <Share2 size={13} /> Share
          </button>
          <button onClick={handleDownloadPdf} disabled={pdfBusy} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px', color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            <Download size={13} /> Download as PDF
          </button>
        </div>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`, borderRadius: 18, padding: '18px 20px', position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{
            position: 'absolute', top: 18, right: 20, zIndex: 1,
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 9,
            padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <CheckCircle2 size={11} color="#fff" strokeWidth={3} />
            <span style={{ color: '#fff', fontSize: 10.5, fontWeight: 800 }}>{d.socialProfilesFound}/{platforms.length} Found</span>
          </div>
          <div style={{ position: 'relative', paddingRight: 100 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <ShieldCheck size={15} color="#fff" strokeWidth={2.5} />
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Social Intelligence · Credit Report
              </span>
            </div>
            <h1 style={{ color: '#fff', fontSize: 17, fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              Digital Footprint
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'rgba(255,255,255,0.75)', fontFamily: MONO }}>
                {data.mobile}
              </span>
            </h1>
          </div>
        </div>

        {/* Phone Number Digital Age */}
        <SectionCard title="Phone Number Digital Age" icon={Smartphone}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={{ fontSize: 24, fontWeight: 800, color: INK }}>{d.digitalAge.age}</span>
            <span style={{ fontSize: 12.5, color: SLATE, fontWeight: 600 }}>
              years on network <span style={{ color: SLATE, fontWeight: 500 }}>(since {formatMonthYear(d.digitalAge.month, d.digitalAge.year)})</span>
            </span>
          </div>
          <p style={{ fontSize: 10, color: SLATE, margin: 0, paddingTop: 8, borderTop: `1px solid ${LINE}`, lineHeight: 1.5 }}>
            As per telecom records: {d.phoneMeta.operator}, {d.phoneMeta.circle} circle, connection type {d.phoneMeta.isPostpaid === 'UNKNOWN' ? 'unknown' : d.phoneMeta.isPostpaid.toLowerCase()}.
            Operator/circle can lag actual porting or relocation — treat as reference only, not current status.
          </p>
        </SectionCard>

        {/* Platform grid — two columns */}
        <SectionCard
          title={`Digital Platforms (${platforms.length})`}
          footer={
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: SLATE, fontWeight: 600 }}>
                <b style={{ color: GREEN }}>{yesCount}</b> found · <b style={{ color: RED }}>{noCount}</b> not found · <b style={{ color: SLATE }}>{naCount}</b> no signal
              </span>
            </div>
          }
        >
          <div className="si-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
            {platforms.map(p => (
              <div key={p.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${LINE}` }}>
                <span style={{ fontSize: 12, color: INK, fontWeight: 600 }}>{p.label}</span>
                <StatusChip state={p.state} />
              </div>
            ))}
          </div>
        </SectionCard>

        <p style={{ fontSize: 13, fontWeight: 700, color: INK, textAlign: 'center', marginTop: 4 }}>
          {d.socialProfilesFound} out of {platforms.length} Social Profile Accounts Available
        </p>
      </div>
    </div>
  );
}
